// components/SolarParametersSection.tsx
import { Panels } from '@gui/constants/Panels';
import usePersistentStore, { getGlobalState, useGlobalBucket } from '@gui/store/persistent/persistentStore';
import type { DeepPartial, SolarEnergyResourcesState } from '@visualizer/types';
import { Box, Checkbox, Divider, FormControlLabel, Slider, Typography } from '@mui/material';
import { grid } from 'Global/global_variables';
import React, { useEffect } from 'react';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { getGrid } from 'store/LoadingStore';
import { BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import type { SolarParamsBlockParameters } from '@data/Simulations/pipeline/blocks/SolarParamsBlock';

const SolarParametersSection: React.FC = () => {
    // Zustand State Variables:
    const solarParams = useGlobalBucket((state) => state.solarEnergyResources.solarParams);

    // Zustand Actions:
    const setPanel = usePersistentStore((s) => s.setPanel);
    const setSolarEnergyPanel = (updates: DeepPartial<SolarEnergyResourcesState>) => setPanel(updates, Panels.SOLAR_ENERGY_RESOURCES);
    const pipelineController = ControllerManager.getInstance().getPipelineController();
    const solarBlockId = usePersistentStore((state) => {
        const id = state.layers.activeLayerID;
        if (!id) return undefined;
        return state.layers.layerStates[id]?.pipeline.blocks.find((block) => block.type === BlockKeys.SolarParameters)?.id;
    });

    useEffect(() => {
        if (solarParams.unitOfMeasure === 0) return;
        setSolarEnergyPanel({
            solarParams: {
                unitOfMeasure: 0,
            },
        });
    }, [solarParams.unitOfMeasure, setSolarEnergyPanel]);

    const syncSolarParamsBlock = () => {
        const activeLayer = getGrid()?.getActiveLayer?.();
        if (!activeLayer || !activeLayer.isSun?.()) return;

        const resolvedId = solarBlockId ?? pipelineController.getBlockIdByType(BlockKeys.SolarParameters);
        if (!resolvedId) return;

        const normalizePercent = (value: number) => (value > 0 ? value / 100 : value);
        const nextSolarParams = getGlobalState().solarEnergyResources.solarParams;

        pipelineController.updateBlockParams<SolarParamsBlockParameters>(resolvedId, {
            enabled: true,
            isCloudCoverOn: !!nextSolarParams.cloudCoverage.on,
            cloudCoverPercentage: normalizePercent(nextSolarParams.cloudCoverage.value ?? 0),
            isDiffuseRadiationOn: !!nextSolarParams.diffuseRadiation.on,
            diffuseRadiationPercentage: normalizePercent(nextSolarParams.diffuseRadiation.value ?? 0),
        });
    };

    // Handlers:
    type SolarParamKey = 'cloudCoverage' | 'diffuseRadiation';

    const handleCheckbox = (key: SolarParamKey) => {
        setSolarEnergyPanel({
            solarParams: {
                [key]: { on: !solarParams[key].on },
            },
        });

        const frame = grid.getActiveLayer().getActiveFrame();

        frame.updateShadows();
        syncSolarParamsBlock();
    };

    const handleSlider = (key: SolarParamKey, value: number | number[]) => {
        const val = Array.isArray(value) ? value[0] : value;
        setSolarEnergyPanel({
            solarParams: {
                [key]: { value: val },
            },
        });

        const frame = grid.getActiveLayer().getActiveFrame();
        frame.updateShadows();
        syncSolarParamsBlock();
    };

    // Component HTML:
    return (
        <Box sx={{ px: 1, py: 0.5, mb: 1 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography fontWeight="bold" gutterBottom align="center">
                Solar Parameters
            </Typography>
            <Typography sx={{ fontSize: '9px', textAlign: 'center', mt: -0.5, mb: 0.75, opacity: 0.7 }}>Yearly average</Typography>
            <Divider sx={{ mb: 1 }} />

            {/* Cloud Coverage */}
            <FormControlLabel
                sx={{ my: 0 }}
                control={<Checkbox checked={solarParams.cloudCoverage.on} onChange={() => handleCheckbox('cloudCoverage')} size="small" />}
                label={<Typography sx={{ fontSize: '10px' }}>Cloud Coverage</Typography>}
            />
            {solarParams.cloudCoverage.on && (
                <Slider
                    className="panel-slider"
                    size="small"
                    value={solarParams.cloudCoverage.value}
                    min={solarParams.cloudCoverage.minValue}
                    max={solarParams.cloudCoverage.maxValue}
                    step={1}
                    valueLabelDisplay="on"
                    onChange={(_, val) => handleSlider('cloudCoverage', val)}
                    sx={{ mb: 0.75 }}
                />
            )}

            {/* Diffuse Radiation */}
            <FormControlLabel
                sx={{ my: 0 }}
                control={
                    <Checkbox checked={solarParams.diffuseRadiation.on} onChange={() => handleCheckbox('diffuseRadiation')} size="small" />
                }
                label={<Typography sx={{ fontSize: '10px' }}>Diffuse Radiation</Typography>}
            />
            {solarParams.diffuseRadiation.on && (
                <Slider
                    className="panel-slider"
                    size="small"
                    value={solarParams.diffuseRadiation.value}
                    min={solarParams.diffuseRadiation.minValue}
                    max={solarParams.diffuseRadiation.maxValue}
                    step={1}
                    valueLabelDisplay="on"
                    onChange={(_, val) => handleSlider('diffuseRadiation', val)}
                    sx={{ mb: 0.5 }}
                />
            )}
        </Box>
    );
};

export default SolarParametersSection;
